import {
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  HStack,
  Text,
  Spinner,
  VStack,
  Image,
  Stack,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import dayjs from "dayjs";
import {
  KYDInternalCampaign,
  KYDCampaignMetricsSummary,
} from "@common/interfaces/KYDCampaign";
import { KYDScope } from "@common/interfaces/ICognito";
import { KYDOrganizationFull } from "@common/interfaces/KYDOrganization";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";
import StatCard from "../Components/StatCard";
import DraftCampaigns from "./DraftCampaigns";
import CampaignsSentTable from "./CampaignsSentTable";

const EmailCampaignsTab = ({
  permissions = [],
  organization,
  campaigns,
  summary,
  loadingDashboard,
  onRefresh,
}: {
  permissions?: KYDScope[];
  organization?: KYDOrganizationFull;
  campaigns: KYDInternalCampaign[];
  summary?: KYDCampaignMetricsSummary;
  loadingDashboard: boolean;
  onRefresh: () => Promise<void>;
}) => {
  return (
    <>
      <HStack justifyContent={"space-between"} w="100%">
        <Text fontSize="32px" fontWeight="400" mb={"32px"}>
          Email Overview
        </Text>
        <ShowIfHasScope
          permissions={permissions}
          checkFor={["crm/campaigns:write", "crm/write"]}
        >
          <Link to={"/campaigns/templates"}>
            <Button
              bgColor={"#000000"}
              color={"#FFFFFF"}
              borderRadius={"1000px"}
              fontWeight={"600"}
              fontSize={"16px"}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={"10px"}
              padding={"14px 16px 14px 24px"}
              whiteSpace={"normal"}
            >
              <Text>New Campaign</Text>
              <FiPlus size={24} />
            </Button>
          </Link>
        </ShowIfHasScope>
      </HStack>

      {loadingDashboard ? (
        <VStack
          h="100%"
          w="100%"
          justifyContent="center"
          alignItems="center"
          py={4}
        >
          <Spinner />
        </VStack>
      ) : (
        <Stack>
          <VStack mt={4} w="full" alignItems={"flex-start"} gap={0}>
            <Text fontSize="24px" fontWeight="600">
              Last 30 Days
            </Text>
            {summary?.created_at && (
              <Text fontSize="xs" color="gray.500">
                Last updated on{" "}
                {dayjs(summary.created_at)
                  .tz(organization?.settings.timezone || "America/New_York")
                  .format("dddd, MMMM Do")}{" "}
                at{" "}
                {dayjs(summary.created_at)
                  .tz(organization?.settings.timezone || "America/New_York")
                  .format("h:mmA")}
              </Text>
            )}
          </VStack>

          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            gap={"24px"}
            alignItems="stretch"
          >
            <StatCard title="Emails Sent" value={summary?.display?.sends} />
            <StatCard
              title="Revenue"
              value={summary?.display?.revenue}
              isKYD
              highlight
            />
            <StatCard
              title="Tickets"
              value={summary?.display?.tickets}
              isKYD
              highlight
            />
            <StatCard
              title="Campaigns Sent"
              value={summary?.display?.campaigns_sent}
            />
            <StatCard title="Open Rate" value={summary?.display?.open_rate} />
            <StatCard
              title="Click Through Rate"
              value={summary?.display?.click_through_rate}
            />
            <StatCard title="Complaints" value={summary?.display?.complaints} />
            <StatCard
              title="Unsubscribes"
              value={summary?.display?.unsubscribes}
            />
          </Grid>

          <Badge
            boxShadow={"0px 2px 4px 0px #00000009"}
            mt={"12px"}
            bgColor={"#D3F227"}
            borderRadius={"1000px"}
            textColor={"#111111BF"}
            padding={"10px"}
            fontSize={"12px"}
            fontWeight={"500"}
            textAlign={"center"}
            width={"fit-content"}
            height={"31px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={"4px"}
            textTransform={"none"}
          >
            <Image
              src="/crown.png"
              alt="KYD"
              opacity={0.7}
              width={"16px"}
              height={"16px"}
            />
            <Text mr={0.5} ml={0.5}>
              =
            </Text>
            <Text as="span" display="inline" whiteSpace="nowrap">
              KYD Sold Tickets
            </Text>
          </Badge>
        </Stack>
      )}

      <Box
        display={
          campaigns.filter((c) => c.status === "DRAFT").length > 0
            ? "flex"
            : "none"
        }
        position="relative"
        width={"100%"}
      >
        <ShowIfHasScope
          permissions={permissions}
          checkFor={["crm/campaigns:write", "crm/write"]}
        >
          <>
            <DraftCampaigns
              campaigns={campaigns.filter((c) => c.status === "DRAFT")}
              permissions={permissions}
              invalidateCampaigns={onRefresh}
            />
            <Box
              position="absolute"
              right="0"
              top="0"
              bottom="0"
              width="100px"
              background="linear-gradient(90deg, rgba(255,255,255,0) 0%, #FFFFFF 100%)"
              pointerEvents="none"
            />
          </>
        </ShowIfHasScope>
      </Box>
      <Divider borderColor={"#0000001A"} mt={"24px"} mb={"24px"} />
      <Text fontSize="24px" fontWeight="600" mb={"12px"}>
        Campaigns
      </Text>
      <Box
        w={"100%"}
        overflowX={"auto"}
        maxWidth={"100%"}
        overflowY={"auto"}
        maxHeight={"750px"}
      >
        {campaigns.length > 0 ? (
          <CampaignsSentTable
            permissions={permissions}
            campaigns={campaigns.filter((c) => c.status !== "DRAFT")}
            invalidateCampaigns={onRefresh}
            timezone={
              organization?.settings.timezone || "America/New_York"
            }
          />
        ) : (
          <Text color="gray.500">No campaigns created yet</Text>
        )}
      </Box>
    </>
  );
};

export default EmailCampaignsTab;
